import AddIcon from '@mui/icons-material/Add';
import SimCardAlertIcon from '@mui/icons-material/SimCardAlert';
import { Box, CircularProgress, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { createSearchParams } from 'react-router-dom';

import { Tooltip } from '@work4all/components/lib/components/tooltip/Tooltip';

import { Link, useUser } from '@work4all/data';

import { User } from '@work4all/models/lib/Classes/User.entity';

import { canViewOtherCalendars } from '@work4all/utils/lib/permissions';

import { settings, useSetting } from '../../../../../settings';
import { Calendar } from '../../../../calendar/CalendarLazy';
import { useCalendarInMemoryState } from '../../../../calendar/hooks/use-calendar-in-memory-state';
import { useInitialUsersDataProvider } from '../../../../calendar/hooks/use-initial-users-data-provider';
import { CollapsibleCard } from '../../CollapsibleCard/CollapsibleCard';
import { CardWidgetProps } from '../types';

import { IndicatorContainer } from './components/IndicatorContainer';
import { UserButton } from './components/UserButton';
import { useBillableAppointments } from './hooks/use-billable-appointments';

interface OpenVacationMaskOptions {
  appointmentId?: number | null;
  presetFields?: string | null;
}

export interface CalendarCardProps extends CardWidgetProps {
  onOpenAppointmentMask: (options: OpenVacationMaskOptions) => void;
  forceMobileView?: boolean;
  hideCollapseButton?: boolean;
  hideAddButton?: boolean;
}

const EMPTY_ARRAY = [];
export function CalendarCard(props: CalendarCardProps) {
  const {
    onOpenAppointmentMask,
    forceMobileView = false,
    hideCollapseButton,
    hideAddButton,
    ...others
  } = props;
  const { collapsed } = others;

  const { t } = useTranslation();

  const calendar = useCalendarInMemoryState();

  const { value: currentUserId, set: setCurrentUserId } = useSetting(
    settings.homepageCalendarUserId()
  );
  const { value: lastCalendarParams, set: setLastCalendarParams } = useSetting(
    settings.calendarLastViewParams()
  );

  const initialCalendarUser = useInitialUsersDataProvider({
    userIds: [currentUserId],
    disabled: !currentUserId,
  });

  const user = useUser();

  const currentUser = {
    ...user,
    id: user.benutzerCode,
  } as User;

  const selectedUser = initialCalendarUser.data?.[0] ?? currentUser;

  const locked = !canViewOtherCalendars(user) || initialCalendarUser.loading;
  const { data: billableAppointments, onClick } = useBillableAppointments({
    user: selectedUser,
  });

  const handleUserChange = (user: User) => {
    setCurrentUserId(user.id);
    setLastCalendarParams({
      ...lastCalendarParams,
      userIds: [user.id],
    });
  };

  function maybeRenderHeaderContent() {
    if (!calendar.isReady || initialCalendarUser.loading) return null;

    const params = createSearchParams({
      userIds: selectedUser.id.toString(),
    });

    return (
      <>
        {collapsed && <IndicatorContainer users={[selectedUser]} />}
        {billableAppointments.data?.length ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Tooltip
              title={t('APPOINTMENT.BILLABLE_TERMIN_INFO_HOME', {
                count: billableAppointments.data?.length,
              })}
            >
              <IconButton onClick={onClick}>
                <SimCardAlertIcon color="error" />
              </IconButton>
            </Tooltip>
          </div>
        ) : null}
        <UserButton
          currentUser={currentUser}
          selectedUser={selectedUser}
          onUserChange={handleUserChange}
          locked={locked}
        />

        {!hideAddButton && (
          <IconButton
            data-test-id="add-appointment-btn"
            component={Link}
            to={`details/appointment/new?${params}`}
            size="medium"
          >
            <AddIcon />
          </IconButton>
        )}
      </>
    );
  }

  function maybeRenderCalendar() {
    if (!calendar.isReady || initialCalendarUser.loading) {
      return (
        <Box sx={{ flex: '1', display: 'grid', placeItems: 'center' }}>
          <CircularProgress />
        </Box>
      );
    }

    const calendarProps = calendar.getCalendarProps();

    return (
      <Calendar
        {...calendarProps}
        users={[selectedUser]}
        focusedUserIds={EMPTY_ARRAY}
        forceMobileView={forceMobileView}
        onOpenMask={(appointmentId, presetFields) => {
          onOpenAppointmentMask({ appointmentId, presetFields });
        }}
        hideMobileAddButton
        hideMobileUserSelection
      />
    );
  }

  return (
    <CollapsibleCard
      title={
        currentUser.id !== selectedUser.id
          ? t('CALENDAR.VIEWS.APPOINTMENTS')
          : t('CALENDAR.VIEWS.MY_APPOINTMENTS')
      }
      href="/more/calendar"
      headerContent={maybeRenderHeaderContent()}
      hideCollapseButton={hideCollapseButton}
      {...others}
    >
      <Box
        sx={(theme) => ({
          minHeight: 300,
          display: 'flex',
          flex: '1 0 auto',
          position: 'relative',
          [theme.breakpoints.up('xl')]: {
            minHeight: 0,
          },
        })}
      >
        {maybeRenderCalendar()}
      </Box>
    </CollapsibleCard>
  );
}
